import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import apiClient from '@/lib/api-client'
import { useOrgStore } from '@/lib/store'
import {
  readSeenNoticeIds,
  seenNoticesStorageKey,
  takeUnseenNotices,
  type StoreSyncAddedNotice,
} from '@/lib/store-sync-notices'

const POLL_INTERVAL_MS = 60_000

function browserStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function StoreSyncAddedToasts() {
  const activeOrgId = useOrgStore((state) => state.activeOrgId)
  const queryClient = useQueryClient()
  const { data } = useQuery<StoreSyncAddedNotice[]>({
    queryKey: ['tracker', 'product-sync-jobs', 'added-notices', activeOrgId],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/tracker/product-sync-jobs/added-notices/')
      return (response.data?.results || []) as StoreSyncAddedNotice[]
    },
    enabled: Boolean(activeOrgId),
    refetchInterval: POLL_INTERVAL_MS,
  })

  useEffect(() => {
    if (!activeOrgId || !data) return
    const storage = browserStorage()
    const key = seenNoticesStorageKey(activeOrgId)
    const { unseen, nextSeenIds } = takeUnseenNotices(data, readSeenNoticeIds(storage, key))
    try {
      storage?.setItem(key, JSON.stringify(nextSeenIds))
    } catch {
      // Without storage the toast may repeat on reload; it is still shown.
    }
    if (!unseen.length) return
    unseen.forEach((notice) => (notice.kind === 'presence' ? toast.info(notice.message) : toast.success(notice.message)))
    // New or missing products: let open product lists and sync banners refresh.
    queryClient.invalidateQueries({ queryKey: ['tracker', 'product-sync-jobs', 'active'] })
    queryClient.invalidateQueries({ queryKey: ['tracker', 'owned-products', activeOrgId] })
  }, [activeOrgId, data, queryClient])

  return null
}
